import { FetchPolicy } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { useAllTaxonomyItemsLazyQuery } from '@generated/graphql'
import { defaultLanguageIdSelector } from '@store/slices/languages/selectors'
import { currentNetworkIdSelector } from '@store/slices/networks/selectors'
import {
  setAllTaxonomyItems,
  setAllTaxonomyItemsError,
  setAllTaxonomyItemsLoading,
} from '@store/slices/taxonomies'
import { useAppDispatch, useAppSelector } from '@store/typedHooks'
import { useCallback, useEffect } from 'react'

export function useFetchAllTaxonomyItems(refetchOnly: boolean = false) {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth0()
  const defaultLanguageId = useAppSelector(defaultLanguageIdSelector)
  const currentNetworkId = useAppSelector(currentNetworkIdSelector)

  const [query, { refetch, called }] = useAllTaxonomyItemsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    // fetchPolicy: 'cache-first', // Use cache for initial load
    onCompleted: ({ taxonomyItems }) => {
      dispatch(setAllTaxonomyItems(taxonomyItems))
    },
    onError: error => {
      console.error('Unable to load taxonomies', error)
      dispatch(setAllTaxonomyItemsError(error.message))
    },
  })

  useEffect(() => {
    if (!isAuthenticated || !defaultLanguageId || refetchOnly) {
      return
    }

    const where: any = currentNetworkId
      ? {
          pools: {
            some: {
              networks: { some: { id: { equals: currentNetworkId } } },
            },
          },
        }
      : {}

    dispatch(setAllTaxonomyItemsLoading(true))
    if (called) {
      refetch({
        languageId: defaultLanguageId,
        where,
      })
    } else {
      query({
        variables: {
          languageId: defaultLanguageId,
          where,
        },
      })
    }
  }, [
    dispatch,
    query,
    refetchOnly,
    isAuthenticated,
    defaultLanguageId,
    currentNetworkId,
    called,
  ])

  const _refetch = useCallback(async () => {
    const where: any = currentNetworkId
      ? {
          pools: {
            some: {
              networks: { some: { id: { equals: currentNetworkId } } },
            },
          },
        }
      : {}
    if (called) {
      // Force refetch from network and update cache
      await refetch({
        languageId: defaultLanguageId,
        where,
      })
    } else {
      // Initial query uses default cache-first policy
      await query({
        variables: {
          languageId: defaultLanguageId,
          where,
        },
        fetchPolicy: 'network-only' as FetchPolicy,
      })
    }
  }, [refetch, called, query, dispatch, defaultLanguageId, currentNetworkId])

  if (refetchOnly) {
    return {
      refetch: _refetch,
    }
  }
}
